import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Form } from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import { API, showError, showSuccess } from '../../helpers';

const originInputs = {
  name: '',
  models: '',
  large_models: '',
  prompt_token_threshold: 0,
  system_prompt: '',
  description: '',
  status: 1,
};

const EditRoute = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const routeId = params.id;
  const isEdit = routeId !== undefined;
  const [loading, setLoading] = useState(isEdit);
  const [inputs, setInputs] = useState(originInputs);

  const handleCancel = () => {
    navigate('/route');
  };

  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const loadRoute = async () => {
    try {
      const res = await API.get(`/api/route/${routeId}`);
      const { success, message, data } = res.data;
      if (success) {
        setInputs({
          name: data.name,
          models: data.models,
          large_models: data.large_models || '',
          prompt_token_threshold: data.prompt_token_threshold || 0,
          system_prompt: data.system_prompt || '',
          description: data.description || '',
          status: data.status,
        });
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isEdit) {
      loadRoute().then();
    }
  }, []);

  const submit = async () => {
    if (inputs.name === '' || inputs.models === '') {
      showError(t('route.messages.required'));
      return;
    }
    let localInputs = { ...inputs };
    localInputs.prompt_token_threshold = parseInt(
      localInputs.prompt_token_threshold
    );
    if (isNaN(localInputs.prompt_token_threshold)) {
      localInputs.prompt_token_threshold = 0;
    }
    let res;
    if (isEdit) {
      res = await API.put('/api/route/', {
        ...localInputs,
        id: parseInt(routeId),
      });
    } else {
      res = await API.post('/api/route/', localInputs);
    }
    const { success, message } = res.data;
    if (success) {
      if (isEdit) {
        showSuccess(t('route.messages.update_success'));
        navigate('/route');
      } else {
        showSuccess(t('route.messages.create_success'));
        setInputs(originInputs);
      }
    } else {
      showError(message);
    }
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isEdit ? t('route.edit') : t('route.add')}
          </Card.Header>
          <Form loading={loading}>
            <Form.Field>
              <Form.Input
                label={t('route.name')}
                name='name'
                placeholder={t('route.name_hint')}
                onChange={handleInputChange}
                value={inputs.name}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('route.models')}
                name='models'
                placeholder='gpt-4o-mini,gpt-4o'
                onChange={handleInputChange}
                value={inputs.models}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('route.large_models')}
                name='large_models'
                placeholder='gpt-4o'
                onChange={handleInputChange}
                value={inputs.large_models}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('route.prompt_token_threshold')}
                name='prompt_token_threshold'
                type='number'
                onChange={handleInputChange}
                value={inputs.prompt_token_threshold}
              />
            </Form.Field>
            <Form.Field>
              <Form.TextArea
                label={t('route.system_prompt')}
                name='system_prompt'
                onChange={handleInputChange}
                value={inputs.system_prompt}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('route.description')}
                name='description'
                onChange={handleInputChange}
                value={inputs.description}
              />
            </Form.Field>
            <Form.Field>
              <Form.Select
                label={t('route.status')}
                name='status'
                options={[
                  { key: 1, text: t('route.enabled'), value: 1 },
                  { key: 2, text: t('route.disabled'), value: 2 },
                ]}
                onChange={handleInputChange}
                value={inputs.status}
              />
            </Form.Field>
            <Form.Field>
              <Button onClick={handleCancel}>{t('route.cancel')}</Button>
              <Button primary onClick={submit}>
                {t('route.save')}
              </Button>
            </Form.Field>
          </Form>
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditRoute;
